import React, { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Shield, User, Vault } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { email: "", name: "", password: "", role: "member", group_ids: [] };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);

  const groupMap = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([api.get("/users"), api.get("/groups")]);
      setItems(u.data);
      setGroups(g.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, group_ids: [] }); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, password: "", role: u.role, group_ids: u.group_ids || [] });
    setDialogOpen(true);
  };

  const toggleGroup = (gid) => {
    setForm((f) => ({
      ...f,
      group_ids: f.group_ids.includes(gid) ? f.group_ids.filter((x) => x !== gid) : [...f.group_ids, gid]
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, group_ids: form.group_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        if (form.password.length < 6) return toast.error("Senha mínima 6 caracteres");
        await api.post("/users", form);
        toast.success("Usuário criado");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); }
  };

  const onDelete = async () => {
    try { await api.delete(`/users/${toDelete.id}`); toast.success("Usuário removido"); setToDelete(null); fetchAll(); }
    catch (e) { toast.error(errMsg(e, "Erro ao excluir")); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Equipe</div>
          <h2 className="font-display font-bold text-3xl mt-1 text-[#1F1F24]">Usuários</h2>
          <p className="text-gray-600 text-sm mt-1">{items.length} usuário(s) cadastrado(s). Vincule a cofres para conceder acesso a credenciais.</p>
        </div>
        <Button className="vp-btn-primary text-white h-11 px-5 rounded-lg" onClick={openNew} data-testid="btn-new-user">
          <Plus className="w-4 h-4 mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">Nome</th>
              <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">E-mail</th>
              <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">Perfil</th>
              <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">Cofres</th>
              <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">Criado em</th>
              <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold py-3 px-4 pr-5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center text-gray-500 py-10">Carregando...</td></tr> :
              items.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: u.role === "admin" ? "#FF8A00" : "#6B7280" }}>
                        {u.name.slice(0,1).toUpperCase()}
                      </div>
                      <span className="font-semibold text-[#1F1F24]">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className="vp-pill" style={{ background: u.role === "admin" ? "#FFE4CC" : "#E5E7EB", color: u.role === "admin" ? "#9A4700" : "#374151" }}>
                      {u.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      {u.role === "admin" ? "Administrador" : "Membro"}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {(u.group_ids || []).length === 0 ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (u.group_ids || []).map((gid) => {
                        const g = groupMap[gid];
                        if (!g) return null;
                        return (
                          <span key={gid} className="vp-pill" style={{ background: `${g.color}20`, color: g.color }}>
                            <Vault className="w-3 h-3" /> {g.name}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{new Date(u.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="py-3 px-4 text-right pr-5">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(u)} className="p-1.5 text-gray-500 hover:text-[#FF8A00] hover:bg-orange-50 rounded-md" data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
                      {u.id !== me?.id && (
                        <button onClick={() => setToDelete(u)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Defina os dados de acesso, o perfil e os cofres do usuário.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="u-name">Nome</Label>
                <Input id="u-name" data-testid="user-input-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <Label htmlFor="u-email">E-mail</Label>
                <Input id="u-email" type="email" data-testid="user-input-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required disabled={!!editing} />
              </div>
              <div>
                <Label htmlFor="u-password">Senha {editing && <span className="text-xs text-gray-500">(manter em branco)</span>}</Label>
                <Input id="u-password" type="password" data-testid="user-input-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder={editing ? "Não alterar" : "Mínimo 6 caracteres"} />
              </div>
              <div>
                <Label>Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-input-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="member">Membro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Cofres (acesso compartilhado às credenciais)</Label>
              <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto mt-1">
                {groups.length === 0 ? (
                  <div className="text-sm text-gray-500 p-3 text-center">Nenhum cofre cadastrado.</div>
                ) : groups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0" data-testid={`user-group-row-${g.id}`}>
                    <Checkbox checked={form.group_ids.includes(g.id)} onCheckedChange={() => toggleGroup(g.id)} data-testid={`user-group-checkbox-${g.id}`} />
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: `${g.color}20`, color: g.color }}>
                      <Vault className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1F1F24]">{g.name}</div>
                      <div className="text-xs text-gray-500 truncate">{g.description}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Administradores não precisam de cofres — já visualizam tudo.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="vp-btn-primary text-white" data-testid="user-form-submit">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir <strong>{toDelete?.email}</strong>? Todas as permissões individuais dele serão removidas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
